import { useEffect, useState } from 'react';
import { Form } from 'antd';
import { z } from 'zod';
import { useNavigate } from 'react-router-dom';
import { useMessage } from '@context/MessageProvider';
import { signinSchema } from '@validators/outside.schema';
import { useLoginMutation } from './useAuthMutations';
import { getDashboardRoute } from '@utils/routing';
import { useAuthStore } from '@stores/auth.store';
import { isMacOS } from '@utils/osDetection';
import { abortAllSharedHttpInflight } from '@utils/http';
import { resetAppQueryClientCaches } from '@app/appQueryClient';

const SIGNIN_REMEMBER_KEY = 'signin_remember';

function getStoredSigninRemember() {
    try {
        const raw = localStorage.getItem(SIGNIN_REMEMBER_KEY);
        if (!raw) return null;
        const data = JSON.parse(raw);
        if (data && typeof data.remember === 'boolean' && data.remember && data.email) {
            return { email: data.email, password: data.password || '', remember: true };
        }
        return null;
    } catch {
        return null;
    }
}

function setStoredSigninRemember(email, password, remember) {
    if (remember) {
        localStorage.setItem(SIGNIN_REMEMBER_KEY, JSON.stringify({ email, password, remember: true }));
    } else {
        localStorage.removeItem(SIGNIN_REMEMBER_KEY);
    }
}

export const useSignin = () => {
    const [form] = Form.useForm();
    const navigate = useNavigate();
    const { showMessage } = useMessage();
    const loginMutation = useLoginMutation();
    const [uiLoading, setUiLoading] = useState(false);
    const { user, role, isAuthenticated } = useAuthStore();
    const [isRedirecting, setIsRedirecting] = useState(false);

    // After logout / session expiry, drop React Query + stuck axios work so the sign-in screen does not inherit pending fetches.
    useEffect(() => {
        if (!isAuthenticated) {
            abortAllSharedHttpInflight();
            resetAppQueryClientCaches();
        }
    }, [isAuthenticated]);

    useEffect(() => {
        if (isAuthenticated) {
            setIsRedirecting(true);
            const timer = setTimeout(() => {
                if (user?.isOnboarded === false) {
                    navigate('/onboarding', { replace: true });
                    return;
                }
                const userRole = role || user?.role;
                const dashboardRoute = getDashboardRoute(userRole);
                navigate(dashboardRoute, { replace: true });
            }, 800);
            return () => clearTimeout(timer);
        }

        // Auth was cleared (e.g. user just logged out and Signin is still mounted from a
        // pending pre-logout redirect). Drop the "redirecting" loader so the form re-appears
        // instead of leaving the user on a blank screen.
        setIsRedirecting(false);

        try {
            const expired = sessionStorage.getItem('code360_session_expired');
            if (expired) {
                sessionStorage.removeItem('code360_session_expired');
                showMessage('error', 'Session expired. Please log in again.');
            }
        } catch {
            // ignore
        }
        const stored = getStoredSigninRemember();
        if (stored) {
            form.setFieldsValue({
                email: stored.email,
                password: stored.password,
                remember: true,
            });
        }
    }, [form, isAuthenticated, navigate, user, role]);

    const onFinish = async (values) => {
        setUiLoading(true);
        try {
            const parsedValues = signinSchema.parse(values);
            const remember = !!parsedValues.remember;

            await loginMutation.mutateAsync({
                email: parsedValues.email,
                password: parsedValues.password,
            });

            setStoredSigninRemember(parsedValues.email, parsedValues.password, remember);

            const { role, user } = useAuthStore.getState();
            if (user?.isOnboarded === false) {
                navigate('/onboarding', { replace: true });
                return;
            }
            const userRole = role || user?.role;
            const dashboardRoute = getDashboardRoute(userRole);
            navigate(dashboardRoute);
        } catch (error) {
            const isEmailNotVerified =
                error?.message === 'EMAIL_NOT_VERIFIED' || error?.response?.data?.message === 'EMAIL_NOT_VERIFIED';
            if (isEmailNotVerified && values?.email) {
                navigate(`/otp?email=${encodeURIComponent(values.email)}&verify=email`, { replace: true });
                showMessage('info', 'Please verify your email to continue');
                return;
            }
            if (error instanceof z.ZodError) {
                const fieldErrors = {};
                error.errors.forEach((err) => {
                    fieldErrors[err.path[0]] = err.message;
                });
                form.setFields(
                    Object.keys(fieldErrors).map((field) => ({
                        name: field,
                        errors: [fieldErrors[field]],
                    })),
                );
                showMessage('error', fieldErrors[Object.keys(fieldErrors)[0]]);
                setUiLoading(false);
                return;
            }
            // Non-validation errors already show a toast via mutation onError.
            setUiLoading(false);
        }
    };

    const handleGoogle = () => {};
    const handleApple = () => {};

    return {
        form,
        loading: uiLoading || loginMutation.isPending,
        onFinish,
        handleGoogle,
        handleApple,
        isMac: isMacOS(),
        isRedirecting,
    };
};
